// PRD.279 — Elia AI voice dictation: pure state machine + helpers.
//
// No React, no DOM, no timers — just the transition logic, transcript-insertion
// rules, capability gating, and the stale-callback guard. The React hook
// (useEliaVoiceDictation) drives MediaRecorder and dispatches these actions; all
// the decision logic lives here so it is unit-testable under node:test.

// ─── Friendly, non-technical user messages (PRD.279 "Error messages") ───

export const ELIA_VOICE_MESSAGES = {
  permissionDenied:
    'Microphone access is blocked. Allow microphone access in your browser settings and try again.',
  noSpeech: 'I couldn’t detect any speech. Please try again.',
  transcribeFailed: 'I couldn’t transcribe that recording. Please try again.',
  contextChanged: 'The active patient or visit changed, so the recording was cancelled.',
  unsupported: 'Voice dictation isn’t supported in this browser.',
} as const;

/** Separator inserted between an existing draft and an appended transcript. */
export const TRANSCRIPT_SEPARATOR = '\n\n';

// ─── State model (mirrors the PRD "Suggested state model") ───

export type EliaVoiceStatus = 'idle' | 'recording' | 'transcribing' | 'error';

export type EliaVoiceState =
  | { status: 'idle' }
  | { status: 'recording'; startedAt: number; contextKey: string }
  | { status: 'transcribing'; contextKey: string }
  | { status: 'error'; message: string };

export const ELIA_VOICE_IDLE: EliaVoiceState = { status: 'idle' };

export type EliaVoiceAction =
  /** User pressed the mic; begins a recording bound to `contextKey`. */
  | { type: 'START'; contextKey: string; startedAt: number }
  /** User pressed Stop; recording ends and transcription begins. */
  | { type: 'STOP' }
  /** User pressed Cancel; discard everything and return to idle. */
  | { type: 'CANCEL' }
  /** Transcription returned usable text. */
  | { type: 'SUCCESS' }
  /** Transcription returned nothing / an empty blob. */
  | { type: 'NO_SPEECH' }
  /** Transcription request failed. */
  | { type: 'FAILURE' }
  /** Mic permission denied or the recorder could not start. */
  | { type: 'RECORDING_FAILED' }
  /** Active patient/visit context changed while an operation was live. */
  | { type: 'CONTEXT_CHANGED' }
  /** Browser cannot record / transcribe. */
  | { type: 'UNSUPPORTED' }
  /** Dismiss the current error, returning to idle. */
  | { type: 'DISMISS_ERROR' };

function errorState(message: string): EliaVoiceState {
  return { status: 'error', message };
}

/**
 * Pure transition function. Invalid transitions (e.g. STOP while idle) are
 * ignored and return the current state unchanged, so a stray dispatch can never
 * corrupt the machine.
 */
export function eliaVoiceReducer(state: EliaVoiceState, action: EliaVoiceAction): EliaVoiceState {
  switch (action.type) {
    case 'START':
      // Only from a settled state — never interrupt a live recording/transcribe
      // (prevents multiple simultaneous recordings).
      if (state.status === 'idle' || state.status === 'error') {
        return { status: 'recording', startedAt: action.startedAt, contextKey: action.contextKey };
      }
      return state;

    case 'STOP':
      if (state.status === 'recording') {
        return { status: 'transcribing', contextKey: state.contextKey };
      }
      return state;

    case 'CANCEL':
      return ELIA_VOICE_IDLE;

    case 'SUCCESS':
      if (state.status === 'transcribing') return ELIA_VOICE_IDLE;
      return state;

    case 'NO_SPEECH':
      if (state.status === 'recording' || state.status === 'transcribing') {
        return errorState(ELIA_VOICE_MESSAGES.noSpeech);
      }
      return state;

    case 'FAILURE':
      if (state.status === 'transcribing') return errorState(ELIA_VOICE_MESSAGES.transcribeFailed);
      return state;

    case 'RECORDING_FAILED':
      if (state.status === 'recording' || state.status === 'idle') {
        return errorState(ELIA_VOICE_MESSAGES.permissionDenied);
      }
      return state;

    case 'CONTEXT_CHANGED':
      // Only meaningful while an operation is live; otherwise leave state alone.
      if (state.status === 'recording' || state.status === 'transcribing') {
        return errorState(ELIA_VOICE_MESSAGES.contextChanged);
      }
      return state;

    case 'UNSUPPORTED':
      return errorState(ELIA_VOICE_MESSAGES.unsupported);

    case 'DISMISS_ERROR':
      if (state.status === 'error') return ELIA_VOICE_IDLE;
      return state;

    default:
      return state;
  }
}

// ─── Transcript insertion (PRD.279 FR-5 / "Transcript ready") ───

/**
 * Append a transcript to the existing composer draft.
 *  - trims the transcript; an empty/whitespace transcript inserts nothing;
 *  - an empty draft becomes just the transcript;
 *  - otherwise: `<draft><two newlines><transcript>` (trailing draft whitespace
 *    normalized so the separator is exactly two newlines).
 * Pure and deterministic; the hook's generation guard prevents double-calls.
 */
export function appendTranscript(draft: string, transcript: string): string {
  const clean = transcript.trim();
  if (!clean) return draft; // never insert an empty transcript
  if (!draft.trim()) return clean; // empty draft → transcript only
  return `${draft.replace(/\s+$/, '')}${TRANSCRIPT_SEPARATOR}${clean}`;
}

// ─── Capability / gating (PRD.279 FR-2) ───

export interface DictationAvailabilityInput {
  /** Browser can record audio (MediaRecorder + a supported MIME type). */
  supported: boolean;
  /** Voice-to-text backend endpoint is configured. */
  configured: boolean;
  /** An Elia message send is currently in flight. */
  sending: boolean;
  status: EliaVoiceStatus;
}

/** True when the mic may START a new recording (idle, or retrying after an error). */
export function canStartDictation(input: DictationAvailabilityInput): boolean {
  const settled = input.status === 'idle' || input.status === 'error';
  return input.supported && input.configured && !input.sending && settled;
}

/**
 * True when the mic control should exist at all. When unsupported/unconfigured
 * the composer hides or disables it rather than letting the user enter a broken
 * flow (FR-2).
 */
export function isDictationAvailable(
  input: Pick<DictationAvailabilityInput, 'supported' | 'configured'>,
): boolean {
  return input.supported && input.configured;
}

// ─── Context + stale-callback guards (PRD.279 FR-6 / FR-7) ───

/** The effective context changed away from the one captured at record start. */
export function contextChanged(capturedKey: string, currentKey: string): boolean {
  return capturedKey !== currentKey;
}

/**
 * Reject a stale async result. The hook bumps a generation counter on every
 * start/cancel/context-change; a transcription callback captures the generation
 * it was issued under and is dropped when it no longer matches. This covers
 * duplicate callbacks, results arriving after Cancel, and cross-context leakage.
 */
export function isResultStale(resultGeneration: number, currentGeneration: number): boolean {
  return resultGeneration !== currentGeneration;
}
